// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import {
  useCreateCompressionMutation,
  useFetchCompressionResourceInputQuery,
  useFetchSiteInfoByIdQuery,
} from 'apis/compressions_user/compressionUserApi';
import BigNumber from 'bignumber.js';
import Toggle from 'components/atoms/Toggle';
import { ConfirmModal } from 'components/organisms/ConfirmModal';
import { FormApi } from 'final-form';
import i18n from 'i18n';
import { useEffect, useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectUser } from 'redux/slices/userSlice';
import { getFormatDateWithCurrentTimezone } from 'ts/formatDate';
import {
  CompressionSiteType,
  OperatorType,
  ProcessingMode,
  ScaleType,
} from 'util/Enums';
import {
  DATE_FORMAT,
  MAXIMUM_TARE_WEIGHT_WITHOUT_SCALE_MODEL,
  REGEX_OBJECT,
} from 'util/ConstantValues';
import {
  Option,
  PreRegisSelectedResource,
  ResourcePreRegisFilter,
  SelectedResources,
  UserCompressionCreateRequest,
} from 'util/Types';
import { normalizeNumber } from 'util/commons';
import { isError } from 'util/errorHandler';
import { useConnectToScale } from 'util/hooks/useConnectToScale';
import { validateMaxMinValue, validateRequiredField } from 'util/validator';
import PreRegisResourceSelect from '../PreRegisResourceSelect';
import styles from './CompressionRegistration.module.scss';
import { useWeightInput } from 'util/hooks/useScaleWeightInput';
import RegisUnderWeightAlertModal from 'components/molecules/RegisUnderWeightAlertModal';
import CompressionInfoForm from './CompressionInfoForm';
import { InputDataListField } from 'components/molecules/InputDataListField';
import { getMaterialNamesBySite } from 'apis/operator_site/siteApi';

export type Compression = {
  compressionId: string;
  datetime: string;
  userId: string;
  userName: string;
  weight: string;
  description: string;
  grade: { id: number; name: string };
  gradeId: number;
  siteId: string;
};
interface Props {
  setRefresh?: (isRefresh: boolean) => void;
  isRefresh?: boolean;
}

const CompressionRegistration: React.FC<Props> = ({
  setRefresh,
  isRefresh,
}) => {
  const { t } = useTranslation();

  const user = useSelector(selectUser);
  const isAuto = user.processingMode === ProcessingMode.Auto;

  const [step, setStep] = useState<'info' | 'weight' | 'regis'>(
    isAuto ? 'info' : 'regis'
  );
  const [regisValues, setRegisValues] = useState<PreRegisSelectedResource[]>(
    []
  );
  const [autoMaterialNameOptions, setAutoMaterialNameOptions] = useState<
    Option[]
  >([]);
  const [totalAutoInputResourcesWeight, setTotalAutoInputResourcesWeight] =
    useState<number>(0);
  const [selectedResources, setSelectedResources] = useState<SelectedResources>(
    {}
  );
  const [filter, setFilter] = useState<ResourcePreRegisFilter>({});
  const [isUnderWeightRegistering, setIsUnderWeightRegistering] =
    useState<boolean>(false);
  const [createCompression] = useCreateCompressionMutation();
  const { data: categories } = useFetchSiteInfoByIdQuery(user?.siteId, {
    skip: !user.siteId,
  });
  const { scaleConnected, handleChangeScaleConnectionStatus } =
    useConnectToScale({
      scaleUsingCondition: step === 'weight',
    });

  const [confirm, setConfirm] = useState<{
    open: boolean;
    alert?: string;
    error?: string;
  }>();
  const [prevProcessData, setPrevProcessData] = useState<{
    prevProcessingId: string;
    prevRegisWeight: BigNumber;
    materialName: string;
    type: string;
  }>();

  const { data, refetch: refetchInputResources } =
    useFetchCompressionResourceInputQuery();

  const { weightInputRef } = useWeightInput(
    {
      receiveValueFromScaleCondition: !confirm?.open,
      itemType: 'compression',
    },
    [step]
  );

  const compressionMethod = useMemo(
    (): Option[] =>
      categories?.compressionMethod.map((x: string) => ({
        label: x,
        value: x,
      })) || [],
    [categories]
  );

  const grades = useMemo(
    (): Option[] =>
      categories?.grades.map((x) => ({
        label: x.name,
        value: x.id,
      })) || [],
    [categories]
  );

  const materialTypes = useMemo(
    (): Option[] =>
      categories?.materialTypes.map((x) => ({
        label: x.name,
        value: x.name,
      })) || [],
    [categories]
  );

  const packingStyles = useMemo(
    (): Option[] =>
      categories?.packingStyles.map((x: string) => ({
        label: x,
        value: x,
      })) || [],
    [categories]
  );

  const handleCallFunctionInit = () => {
    try {
      window && window.regisCompressionInitial();
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    handleCallFunctionInit();
  }, []);

  useEffect(() => {
    if (!isAuto) return;
    getMaterialNamesBySite({
      siteId: '',
      screen: 'materialsInProcessing',
      successCallback: (names) => {
        setAutoMaterialNameOptions(
          names.map((name) => ({ label: name, value: name }))
        );
      },
    });
  }, [isAuto]);

  const regisWeight = useMemo(() => {
    return regisValues.reduce((sum, item) => {
      return sum.plus(item.weight);
    }, new BigNumber(0));
  }, [regisValues]);

  const resetFormData = (
    form: FormApi<
      UserCompressionCreateRequest,
      Partial<UserCompressionCreateRequest>
    >
  ) => {
    setRefresh?.(!isRefresh);
    form.restart();
    setSelectedResources({});
    setPrevProcessData(undefined);
    refetchInputResources().unwrap();
  };

  const onSubmit = async (
    values: UserCompressionCreateRequest,
    form: FormApi<
      UserCompressionCreateRequest,
      Partial<UserCompressionCreateRequest>
    >,
    isSubmitContinue?: boolean
  ) => {
    const netWeight = new BigNumber(values.weight || 0)
      .minus(values.tareWeight || 0)
      .toNumber();

    if (!confirm?.open) {
      const total = new BigNumber(values.weight || '').plus(
        prevProcessData?.prevRegisWeight || 0
      );

      const isOverWeight =
        user.processingMode === ProcessingMode.Auto
          ? Number(values.weight) > totalAutoInputResourcesWeight
          : (total.comparedTo(regisWeight) || 0) > 0;

      setConfirm({
        open: true,
        alert: isOverWeight ? t('compressions.alert') || '' : undefined,
        error:
          netWeight < 0
            ? t('emission_input.quantity_exceed_message') || ''
            : undefined,
      });

      return;
    }

    if (netWeight < 0) {
      return;
    }

    createCompression({
      data: {
        ...values,
        weight: netWeight.toString(),
        gradeId: Number(values.gradeId),
        inputResources: regisValues.map(({ processingObjectId, weight }) => ({
          processingObjectId,
          weight,
        })),
        prevProcessingObjectId: prevProcessData?.prevProcessingId,
        tareWeight: values.tareWeight,
      },
      isAuto: user.processingMode === ProcessingMode.Auto,
      isConsecutive:
        !!prevProcessData?.prevProcessingId &&
        (user.processingMode !== ProcessingMode.Auto ||
          (values.materialName === prevProcessData.materialName &&
            values.type === prevProcessData.type)),
    })
      .then((result) => {
        if (isError(result)) return;
        if (result['data']) {
          toast.success(t('messages.M_127'));
          const data = result['data'];
          setConfirm(undefined);
          setIsUnderWeightRegistering(false);

          if (isSubmitContinue) {
            setStep('info');
            setPrevProcessData((prev) => ({
              prevProcessingId: data.id,
              prevRegisWeight:
                user.processingMode !== ProcessingMode.Auto ||
                (values.materialName === prev?.materialName &&
                  values.type === prev.type)
                  ? new BigNumber(netWeight).plus(prev?.prevRegisWeight || 0)
                  : new BigNumber(netWeight),
              materialName: values.materialName,
              type: values.type,
            }));
            form.mutators.setValue('weight', '');
            form.mutators.setValue('tareWeight', '');
          } else {
            setStep(isAuto ? 'info' : 'regis');
            resetFormData(form);
          }

          if (window && window.postCompressions) {
            window.postCompressions(
              JSON.stringify({
                ...data,
                measuredWeight: normalizeNumber({
                  value: values.weight,
                }),
                containerWeight: normalizeNumber({
                  value: values.tareWeight || 0,
                }),
                type: values.type,
                materialName: values.materialName,
                registDatetime: getFormatDateWithCurrentTimezone(
                  data.registDatetime,
                  DATE_FORMAT.slaYMDHms
                ),
              }),
              i18n.language
            );
          }
        }
      })
      .catch((e) => {
        if (e.message && e.message.includes('Android')) {
          return;
        }
        toast.error(
          e?.response?.data?.message || t('compressions.signup_messages.failed')
        );
      });
  };

  const initialValues = useMemo(
    () => ({
      gradeId: grades.length > 0 ? grades[0].value : '',
      materialName:
        (isAuto && user.siteType !== CompressionSiteType.Storage
          ? autoMaterialNameOptions?.[0]?.value
          : regisValues?.[0]?.materialName) || '',
      packingStyle: packingStyles.length > 0 ? packingStyles[0].value : '',
      processingMethod:
        compressionMethod.length > 0 ? compressionMethod[0].value : '',
      type: materialTypes.length > 0 ? materialTypes[0].value : '',
      weight: '0',
      inputResources: [],
    }),
    [
      regisValues,
      grades,
      packingStyles,
      compressionMethod,
      materialTypes,
      data,
      user.processingMode,
      isAuto,
      user.siteType,
      autoMaterialNameOptions,
    ]
  );

  const { weightLeftTitle, weightLeftValue, weightLeftRawValue } =
    useMemo(() => {
      const leftValue =
        user.processingMode === ProcessingMode.Auto
          ? new BigNumber(totalAutoInputResourcesWeight)
          : regisWeight.minus(prevProcessData?.prevRegisWeight || 0);

      return {
        weightLeftTitle:
          prevProcessData?.prevRegisWeight.comparedTo(0) === 1 ||
          user.processingMode === ProcessingMode.Auto
            ? t('pre_regist_resource_select.remaining_input_weight')
            : t('pre_regist_resource_select.input_weight'),
        weightLeftValue: normalizeNumber({
          value: leftValue.comparedTo(0) === 1 ? leftValue : 0,
        }),
        weightLeftRawValue: leftValue,
      };
    }, [
      t,
      regisWeight,
      prevProcessData?.prevRegisWeight,
      user.processingMode,
      totalAutoInputResourcesWeight,
    ]);

  const handleNext = (values: PreRegisSelectedResource[]) => {
    setRegisValues(values);
    setStep('info');
  };

  const handleBack = (
    form: FormApi<
      UserCompressionCreateRequest,
      Partial<UserCompressionCreateRequest>
    >
  ) => {
    setStep('info');
    form.mutators.setValue('weight', '');
    form.mutators.setValue('tareWeight', '');
  };

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLElement).blur();
    }
  };

  const handleRegister = (params: {
    values: UserCompressionCreateRequest;
    form: FormApi<
      UserCompressionCreateRequest,
      Partial<UserCompressionCreateRequest>
    >;
    isUnderWeight?: boolean;
    isContinue?: boolean;
  }) => {
    const { values, form, isUnderWeight, isContinue } = params;

    if (isUnderWeight && user.processingMode !== ProcessingMode.Auto) {
      setIsUnderWeightRegistering(true);
    } else {
      onSubmit(values, form, isContinue);
    }
  };

  return (
    <div className={styles.compressionRegistration}>
      <Form<UserCompressionCreateRequest>
        onSubmit={() => {}}
        mutators={{
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
        }}
        validate={(values) => ({
          weight: validateRequiredField(
            values.weight,
            t('emission_input.label_weight') || ''
          ),
          materialName: validateRequiredField(
            values.materialName,
            t('emission_input.material_name') || ''
          ),
          processingMethod: validateRequiredField(
            values.processingMethod,
            t('emission_input.processing_method') || ''
          ),
          type: validateRequiredField(
            values.type,
            t('emission_input.material_types') || ''
          ),
          gradeId: validateRequiredField(
            values.gradeId,
            t('emission_input.grade') || ''
          ),
          packingStyle: validateRequiredField(
            values.packingStyle,
            t('emission_input.packing_style') || ''
          ),
        })}
        initialValues={initialValues}
      >
        {(props) => {
          const { handleSubmit, form, values, valid } = props;

          const netWeight = new BigNumber(values.weight || 0)
            .minus(values.tareWeight || 0)
            .toNumber();
          const isUnderWeight = weightLeftRawValue.comparedTo(netWeight) === 1;

          return (
            <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
              {!isAuto && (
                <div
                  className={styles.register}
                  style={{
                    // must use CSS here to keep selected resource, page, filter after back from step 2
                    display: step === 'regis' ? 'block' : 'none',
                  }}
                >
                  <PreRegisResourceSelect
                    className={styles.regis}
                    operatorType={OperatorType.Compressions}
                    resourceData={data || []}
                    onNext={handleNext}
                    actionProps={{
                      selectedResources,
                      setSelectedResources,
                      filter,
                      setFilter,
                    }}
                  />
                </div>
              )}

              {step === 'info' && (
                <CompressionInfoForm
                  registeringProcessingObjectIds={regisValues.map(
                    ({ processingObjectId }) => processingObjectId
                  )}
                  resetFormData={resetFormData}
                  setStep={setStep}
                  prevProcessingObjectId={prevProcessData?.prevProcessingId}
                  weightLeftTitle={weightLeftTitle}
                  weightLeftValue={weightLeftValue}
                  materialTypes={materialTypes}
                  compressionMethod={compressionMethod}
                  grades={grades}
                  packingStyles={packingStyles}
                  formProps={props}
                  materialNameOptions={isAuto ? autoMaterialNameOptions : []}
                  totalAutoInputResourcesWeight={totalAutoInputResourcesWeight}
                  setTotalAutoInputResourcesWeight={
                    setTotalAutoInputResourcesWeight
                  }
                />
              )}

              {step === 'weight' && (
                <div className={styles.weightContent}>
                  {user.scalesType !== ScaleType.NONE && (
                    <div className={styles.scaleConnectToggle}>
                      <Toggle
                        content={t('emission_input.connect_with_scale') || ''}
                        value={scaleConnected}
                        setValue={handleChangeScaleConnectionStatus}
                        isDarkMode
                      />
                    </div>
                  )}

                  <div className={styles.weightContainer}>
                    {user.scalesType !== ScaleType.NONE && (
                      <div className={styles.scaleConnectToggleMobile}>
                        <Toggle
                          content={t('emission_input.connect_with_scale') || ''}
                          value={scaleConnected}
                          setValue={handleChangeScaleConnectionStatus}
                          isDarkMode
                        />
                      </div>
                    )}

                    <div className={styles.title}>
                      {t('compressions.label_weight')}
                    </div>
                    <div className={styles.content}>
                      {`${weightLeftTitle}: ${values.materialName} ${weightLeftValue}kg`}
                    </div>
                    <div className={styles.field}>
                      <Field name="weight">
                        {({ input }) => (
                          <input
                            {...input}
                            ref={weightInputRef}
                            className={styles.input}
                            onChange={(e) => {
                              if (
                                e.target.value &&
                                !REGEX_OBJECT.tenDigitsWith2Decimal.test(
                                  e.target.value
                                )
                              ) {
                                return;
                              }
                              input.onChange(e);
                            }}
                          />
                        )}
                      </Field>
                      <div className={styles.unit}>kg</div>
                    </div>
                    <div className={styles.bottom}>
                      <div className={styles.buttons}>
                        <button
                          className={styles.back}
                          onClick={() => handleBack(form)}
                        >
                          {t('common.return')}
                        </button>
                        <button
                          className={styles.submit}
                          onClick={(e) => {
                            e.preventDefault();

                            handleRegister({
                              values,
                              form,
                            });
                          }}
                          disabled={!valid}
                        >
                          {t('common.button.register')}
                        </button>
                      </div>
                    </div>

                    <div className={styles.tareInput}>
                      <InputDataListField
                        placeholder={t('emission_input.container_weight')}
                        name="tareWeight"
                        validRegex={REGEX_OBJECT.fiveDigitsWith2Decimal}
                        isDarkMode
                        className={styles.tareWeightInput}
                        validate={(value) =>
                          validateMaxMinValue({
                            value,
                            field: t('emission_input.container_weight'),
                            maxValue: Number(
                              user.scalesModel?.tareMax ||
                                MAXIMUM_TARE_WEIGHT_WITHOUT_SCALE_MODEL
                            ),
                            checkRequired: false,
                          })
                        }
                      />
                      <div className={styles.tareInputUnit}>kg</div>
                    </div>
                  </div>
                </div>
              )}

              <ConfirmModal
                fullBottomWidth
                title={t('common.alert.confirm')}
                submitAndFinishLabel={
                  t('common.button.continue_to_register') || ''
                }
                submitLabel={t('common.button.processing_completed') || ''}
                alert={confirm?.alert}
                isDisable={netWeight < 0}
                isOpen={confirm?.open && !isUnderWeightRegistering}
                closeEditModal={() => {
                  setConfirm(undefined);
                }}
                onClickSubmitAndFinish={() =>
                  handleRegister({
                    values,
                    form,
                    isContinue: true,
                  })
                }
                onClick={() =>
                  handleRegister({
                    values,
                    form,
                    isUnderWeight,
                  })
                }
                aboveActionNote={
                  <span
                    className={styles.weightLeft}
                  >{`${weightLeftTitle}: ${weightLeftValue}kg`}</span>
                }
                belowActionNote={
                  confirm?.error ? (
                    <span className={styles.weightLeft}>{confirm.error}</span>
                  ) : (
                    isUnderWeight && t('pre_regist_resource_select.action_note')
                  )
                }
                confirmContents={[
                  {
                    label: `${t('emission_input.material_name')}:`,
                    contents: values.materialName,
                  },
                  {
                    label: `${t('emission_input.processing_method')}:`,
                    contents: values.processingMethod,
                  },
                  {
                    label: `${t('emission_input.material_types')}:`,
                    contents: values.type,
                  },
                  {
                    label: `${t('emission_input.grade')}:`,
                    contents:
                      grades.find(
                        (g) => g.value.toString() === values.gradeId.toString()
                      )?.label || '',
                  },
                  {
                    label: `${t('emission_input.packing_style')}:`,
                    contents: values.packingStyle,
                  },
                  ...(values.tareWeight && values.tareWeight !== '0'
                    ? [
                        {
                          label: `${t('emission_input.measured_weight')}:`,
                          contents: `${normalizeNumber({
                            value: values.weight,
                            toFixed: false,
                            commaSplit: true,
                          })}kg`,
                        },
                        {
                          label: `${t('emission_input.container_weight')}:`,
                          contents: `${normalizeNumber({
                            value: values.tareWeight || 0,
                            toFixed: false,
                            commaSplit: true,
                          })}kg`,
                        },
                      ]
                    : []),
                  {
                    label:
                      values.tareWeight && values.tareWeight !== '0'
                        ? t('emission_input.net_weight')
                        : t('emission_input.label_weight'),
                    contents: `${normalizeNumber({
                      value: netWeight,
                      toFixed: false,
                      commaSplit: true,
                    })}kg`,
                  },
                ]}
              />

              {isUnderWeightRegistering && (
                <RegisUnderWeightAlertModal
                  totalWeight={regisWeight.toString()}
                  registeringWeight={netWeight}
                  remainingWeight={weightLeftRawValue.toString()}
                  onCancel={() => setIsUnderWeightRegistering(false)}
                  onOk={() =>
                    handleRegister({
                      values,
                      form,
                    })
                  }
                />
              )}
            </form>
          );
        }}
      </Form>
    </div>
  );
};
export default CompressionRegistration;
